import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Plus, Trash2, XCircle } from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import { EditProjectDialog } from "@/components/portfolio/EditProjectDialog";
import { ColumnPicker, type ColumnDef } from "@/components/ui/ColumnPicker";
import { BriefBadge } from "@/components/ui/BriefBadge";
import { JiraRollupBadge } from "@/components/portfolio/JiraRollupBadge";
import { usePortfolio, useDeleteProject, useUpdateProject, useArchivedProjects } from "@/hooks/usePortfolio";
import { useAppSettings } from "@/hooks/useAppSettings";
import { costLabelFor, readBlendedRate } from "@/lib/cost";
import { formatDate, dueUrgency } from "@/lib/format";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/roles";
import { isRock } from "@/lib/rockQuarter";
import { toast } from "sonner";
import type { Project } from "@/types/project";
import {
  PORTFOLIO_TABS,
  statusLabel,
  statusPillClass,
  isAtRisk,
  isNeedsHelp,
  effectiveHealth,
} from "@/lib/projectStatus";

const PORTFOLIO_COLUMNS: ColumnDef[] = [
  { key: "id", label: "ID", defaultVisible: true },
  { key: "name", label: "Name", defaultVisible: true },
  { key: "source", label: "Source", defaultVisible: true },
  { key: "team", label: "Team", defaultVisible: true },
  { key: "status", label: "Status", defaultVisible: true },
  { key: "health", label: "Health", defaultVisible: true },
  { key: "phase", label: "Phase", defaultVisible: true },
  { key: "priority", label: "Priority", defaultVisible: true },
  { key: "tshirt", label: "T-Shirt", defaultVisible: true },
  { key: "est_cost", label: "Est. Cost", defaultVisible: true },
  { key: "brief", label: "Brief", defaultVisible: true },
  { key: "jira", label: "Jira", defaultVisible: true },
  { key: "hours", label: "Hours", defaultVisible: true },
  { key: "spec_due", label: "Spec Due", defaultVisible: true },
  { key: "spec_completed", label: "Spec Completed", defaultVisible: true },
  { key: "start", label: "Start", defaultVisible: true },
  { key: "end", label: "End", defaultVisible: true },
  { key: "slip", label: "Slip", defaultVisible: true },
  { key: "pct_done", label: "Progress", defaultVisible: true },
];

// Render slip_days as a colored chip. Color tiers chosen so the eye
// jumps to genuine problems (>14d) without amber-noising every routine
// 1-2 day adjustment. `null` means we have no baseline yet — show a
// dash, never a misleading "0".
function SlipCell({ days }: { days: number | null | undefined }) {
  if (days == null) return <span className="text-xs text-ink-faint">{"\u2014"}</span>;
  if (days === 0) return (
    <span className="inline-block rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-500/30">
      On plan
    </span>
  );
  if (days > 0) {
    const cls = days > 14
      ? "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 ring-1 ring-rose-200 dark:ring-rose-500/30"
      : "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/30";
    return (
      <span className={cn("inline-block rounded-full px-2 py-0.5 text-[10px] font-medium tabular-nums", cls)}>
        +{days}d
      </span>
    );
  }
  return (
    <span className="inline-block rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-500/30 tabular-nums">
      {Math.abs(days)}d early
    </span>
  );
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z").getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

// Display subdomain for ticket click-through (matches backend config).
const FRESHSERVICE_PORTAL = "helpdesk.etereman.com";

// ---------------------------------------------------------------------------
// Spec helpers — drive off status now (not health string match)
// ---------------------------------------------------------------------------

function activeSpecDue(p: Project): string | null {
  if (p.status === "technical_spec") return p.technical_spec_due;
  return p.functional_spec_due;
}

function shownSpecCompleted(p: Project): string | null {
  if (p.status === "technical_spec") return p.functional_spec_completed;
  return null;
}

function specDueCellClass(d: string | null | undefined): string {
  const u = dueUrgency(d);
  if (u === "overdue") return "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 font-semibold";
  if (u === "this_week") return "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 font-medium";
  return "text-ink-muted";
}

// ---------------------------------------------------------------------------
// Phase helpers (still keyed off current_phase, unchanged)
// ---------------------------------------------------------------------------

const PHASE_STYLE: Record<string, string> = {
  planning: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300",
  execution: "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
  testing_go_live: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
};
const PHASE_LABEL: Record<string, string> = {
  planning: "Planning",
  execution: "Execution",
  testing_go_live: "Testing / Go Live",
};
function phaseStyle(phase: string): string {
  return PHASE_STYLE[phase.toLowerCase()] ?? "bg-raised text-ink-3";
}
function phaseLabel(phase: string): string {
  return PHASE_LABEL[phase.toLowerCase()] ?? phase;
}

const ROLE_DOTS = [
  { key: "pm", short: "PM", label: "Project Manager" },
  { key: "ba", short: "BA", label: "Business Analyst" },
  { key: "functional", short: "FN", label: "Functional" },
  { key: "technical", short: "TC", label: "Technical" },
  { key: "developer", short: "DV", label: "Developer" },
  { key: "infrastructure", short: "IN", label: "Infrastructure" },
  { key: "dba", short: "DB", label: "DBA" },
  { key: "erp", short: "ER", label: "ERP" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface StatusGroup {
  key: string;
  label: string;
  projects: Project[];
}

// "archived" is a synthetic tab — not driven by status, but by the
// project's archived_at flag. Added here so the URL ?tab=archived round-trips.
const VALID_TAB_KEYS = new Set([...PORTFOLIO_TABS.map((t) => t.key), "archived"]);

export function Portfolio() {
  const { isPmo } = useCan();
  const navigate = useNavigate();
  const { data: projects, isLoading, isError, error } = usePortfolio();
  const archivedQuery = useArchivedProjects();
  // Cost rate — single setting drives the Est. Cost column. Pulled here
  // once and threaded into the row render so the column refreshes when
  // Settings → Cost changes the rate.
  const { data: costSettings } = useAppSettings("cost");
  const blendedRate = readBlendedRate(costSettings);
  const deleteProject = useDeleteProject();
  const updateProject = useUpdateProject();

  // All scan-state lives in the URL so:
  //   - back-button restores the exact filter+tab state
  //   - refresh restores the same view
  //   - the URL is shareable ("here's the In Spec slice")
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("q") ?? "";
  const tabFromUrlRaw = searchParams.get("tab");
  // Backward-compat alias: the In-Progress tab key was renamed from
  // "in_build" → "in_progress" when we unified the status key with its
  // user-facing label. Old bookmarks (and any cached deep-links from the
  // Exec page's "View all" buttons) still resolve to the right tab.
  const tabFromUrl = tabFromUrlRaw === "in_build" ? "in_progress" : tabFromUrlRaw;
  const staleOnly = searchParams.get("stale") === "1";
  const initialTab = tabFromUrl && VALID_TAB_KEYS.has(tabFromUrl)
    ? tabFromUrl
    : staleOnly
      ? "in_flight"
      : "in_progress";
  const [statusTab, setStatusTabState] = useState<string>(initialTab);

  // Mutate one URL param at a time without dropping the others. Replace
  // (not push) so the back button doesn't capture every keystroke in the
  // search box.
  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value == null || value === "") next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };
  const setFilter = (v: string) => updateParam("q", v);

  // Keep state in sync if URL changes (e.g. user uses browser back/forward
  // between tabs).
  useEffect(() => {
    if (tabFromUrl && VALID_TAB_KEYS.has(tabFromUrl) && tabFromUrl !== statusTab) {
      setStatusTabState(tabFromUrl);
    }
  }, [tabFromUrl, statusTab]);

  const setStatusTab = (key: string) => {
    setStatusTabState(key);
    updateParam("tab", key);
  };
  const [colsByTab, setColsByTab] = useState<Record<string, Set<string>>>({});
  const visibleCols = colsByTab[statusTab] ?? new Set(PORTFOLIO_COLUMNS.map((c) => c.key));
  const col = (key: string) => visibleCols.has(key);
  const handleColChange = (cols: Set<string>) => {
    setColsByTab((prev) => ({ ...prev, [statusTab]: cols }));
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const openNew = () => {
    setEditProject(null);
    setDialogOpen(true);
  };

  const openEdit = (p: Project) => {
    setEditProject(p);
    setDialogOpen(true);
  };

  const handleDelete = () => {
    if (deleteConfirm) {
      deleteProject.mutate(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  // Rocks (Rock-Quarter-tagged projects) are initiative tasks, not
  // standalone projects — exclude them from the Portfolio list entirely.
  // They live on the Roadmap / Initiatives surfaces. (CFO direction.)
  const list = (Array.isArray(projects) ? projects : []).filter((p) => !isRock(p));
  const filtered = list.filter(
    (p) =>
      (!filter ||
        p.name.toLowerCase().includes(filter.toLowerCase()) ||
        p.id.toLowerCase().includes(filter.toLowerCase())) &&
      (!staleOnly || p.is_stale),
  );

  // Build one StatusGroup per PORTFOLIO_TAB by intersecting `filtered`
  // with the tab's status set. This populates multi-status filter tabs
  // (e.g. "in_flight" = spec + build + testing) the same way as
  // single-status category tabs.
  const groups = useMemo<StatusGroup[]>(() => {
    const result: StatusGroup[] = [];
    for (const tab of PORTFOLIO_TABS) {
      const allowed = new Set(tab.statuses as string[]);
      const projectsInTab = filtered.filter((p) => p.status && allowed.has(p.status));
      if (tab.key === "in_spec") {
        projectsInTab.sort((a, b) => {
          const da = activeSpecDue(a);
          const db = activeSpecDue(b);
          if (!da && !db) return 0;
          if (!da) return 1;
          if (!db) return -1;
          return da.localeCompare(db);
        });
      }
      result.push({ key: tab.key, label: tab.label, projects: projectsInTab });
    }
    // Synthetic Archived tab — sourced from the dedicated archived query
    // (separate fetch from the active list). Filtered the same way the
    // status tabs are so the search box still works on it.
    const archivedAll = archivedQuery.data ?? [];
    const archivedFiltered = archivedAll.filter(
      (p) =>
        !filter ||
        p.name.toLowerCase().includes(filter.toLowerCase()) ||
        p.id.toLowerCase().includes(filter.toLowerCase()),
    );
    result.push({ key: "archived", label: "Archived", projects: archivedFiltered });
    return result;
  }, [filtered, archivedQuery.data, filter]);

  // Count overdue specs in the In Spec tab
  const overdueSpecCount = useMemo(() => {
    const spec = groups.find((g) => g.key === "in_spec");
    if (!spec) return 0;
    return spec.projects.filter((p) => dueUrgency(activeSpecDue(p)) === "overdue").length;
  }, [groups]);

  return (
    <>
      <TopBar title="Portfolio" subtitle="All projects with role allocations and sizing.">
        <ColumnPicker key={statusTab} storageKey={`portfolio_${statusTab}`} columns={PORTFOLIO_COLUMNS} onChange={handleColChange} />
        <input
          type="text"
          placeholder="Search projects..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-lg border border-line px-3 py-1.5 text-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-400"
        />
        <button
          onClick={openNew}
          className="flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-navy-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add project
        </button>
      </TopBar>
      <div className="space-y-4 p-8">
        {isLoading && (
          <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
            Loading projects...
          </div>
        )}
        {isError && (
          <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-6 text-sm text-red-800 dark:text-red-300">
            {(error as Error).message}
          </div>
        )}
        {staleOnly && (
          <div className="flex items-center gap-2 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="font-semibold">Stale plans only</span>
            <span className="text-xs text-amber-700 dark:text-amber-300">
              — showing projects whose plan hasn't been updated recently
            </span>
            <button
              onClick={() => updateParam("stale", null)}
              className="ml-auto rounded-md border border-amber-300 dark:border-amber-500/40 bg-surface px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/15 transition-colors"
            >
              Clear filter
            </button>
          </div>
        )}
        {overdueSpecCount > 0 && (
          <div className="flex items-center gap-2 rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-800 dark:text-red-300">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="font-semibold">
              {overdueSpecCount} spec{overdueSpecCount === 1 ? "" : "s"} overdue
            </span>
            <span className="text-xs text-red-600 dark:text-red-400">
              — due date has passed and no completion recorded
            </span>
            <button
              onClick={() => setStatusTab("in_spec")}
              className="ml-auto rounded-md border border-red-300 dark:border-red-500/40 bg-surface px-2.5 py-1 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/15 transition-colors"
            >
              View In Spec
            </button>
          </div>
        )}
        {/* Status tabs — always show every tab so a filter that empties
            the active tab doesn't make the nav rearrange under the user.
            Counts reflect the current search filter. */}
        <div className="border-b border-line bg-surface rounded-t-xl px-2 overflow-x-auto">
          <nav className="flex gap-0.5">
            {groups.map((g) => {
              const empty = g.projects.length === 0;
              const active = statusTab === g.key;
              return (
                <button
                  key={g.key}
                  onClick={() => setStatusTab(g.key)}
                  className={cn(
                    "flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-xs font-medium transition-colors",
                    active
                      ? "border-brand-navy-600 text-brand-navy-700 dark:text-brand-navy-300"
                      : empty
                        ? "border-transparent text-ink-faint hover:text-ink-muted hover:border-line"
                        : "border-transparent text-ink-muted hover:text-ink-2 hover:border-line-strong",
                  )}
                >
                  {g.label}
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-0.5 text-[9px] font-semibold",
                      empty ? "bg-canvas text-ink-faint" : "bg-raised text-ink-3",
                    )}
                  >
                    {g.projects.length}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Cross-tab search hint — when the active tab has no matches
            but the search returned hits elsewhere, surface where they
            actually are so the user doesn't think the search broke. */}
        {filter.trim().length > 0 && (() => {
          const activeGroup = groups.find((g) => g.key === statusTab);
          if (!activeGroup || activeGroup.projects.length > 0) return null;
          const elsewhere = groups.filter((g) => g.key !== statusTab && g.projects.length > 0);
          if (elsewhere.length === 0) return null;
          return (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-brand-navy-200 dark:border-brand-navy-500/30 bg-brand-navy-50 dark:bg-brand-navy-500/10 px-4 py-3 text-sm text-brand-navy-800 dark:text-brand-navy-300">
              <span className="font-medium">No matches in {activeGroup.label}.</span>
              <span className="text-xs text-brand-navy-600 dark:text-brand-navy-400">Found in:</span>
              {elsewhere.map((g) => (
                <button
                  key={g.key}
                  onClick={() => setStatusTab(g.key)}
                  className="rounded-md border border-brand-navy-300 dark:border-brand-navy-500/40 bg-surface px-2 py-0.5 text-xs font-medium text-brand-navy-700 dark:text-brand-navy-300 hover:bg-brand-navy-100 dark:hover:bg-brand-navy-500/15 transition-colors"
                >
                  {g.label} ({g.projects.length})
                </button>
              ))}
            </div>
          );
        })()}

        {/* Active tab content */}
        {groups.filter((g) => g.key === statusTab).map((group) => (
          <div key={group.key} className="overflow-hidden rounded-b-xl border border-t-0 border-line bg-surface">
            {group.projects.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-ink-muted">No projects in this category.</div>
            ) : (
              <div className="overflow-x-auto border-t border-line-soft">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line-soft text-left text-xs font-medium text-ink-muted">
                      {col("id") && <th className="px-4 py-2">ID</th>}
                      {col("name") && <th className="px-4 py-2">Name</th>}
                      {col("source") && <th className="px-4 py-2">Source</th>}
                      {col("team") && <th className="px-4 py-2">Team</th>}
                      {col("status") && <th className="px-4 py-2">Status</th>}
                      {col("health") && <th className="px-4 py-2">Health</th>}
                      {col("phase") && <th className="px-4 py-2">Phase</th>}
                      {col("priority") && <th className="px-4 py-2">Priority</th>}
                      {col("tshirt") && <th className="px-4 py-2">T-Shirt</th>}
                      {col("est_cost") && <th className="px-4 py-2 text-right">Est. Cost</th>}
                      {col("brief") && <th className="px-4 py-2">Brief</th>}
                      {col("jira") && <th className="px-4 py-2">Jira</th>}
                      {col("hours") && <th className="px-4 py-2 text-right">Hours</th>}
                      {col("spec_due") && <th className="px-4 py-2">Spec Due</th>}
                      {col("spec_completed") && <th className="px-4 py-2">Spec Completed</th>}
                      {col("start") && <th className="px-4 py-2">Start</th>}
                      {col("end") && <th className="px-4 py-2">End</th>}
                      {col("slip") && <th className="px-4 py-2">Slip</th>}
                      {col("pct_done") && <th className="px-4 py-2 text-right">Progress</th>}
                      <th className="px-4 py-2 w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {group.projects.map((p) => {
                      const atRisk = isAtRisk(p.health);
                      const needsHelp = isNeedsHelp(p.health);
                      return (
                      <tr
                        key={p.id}
                        onClick={() => navigate(`/portfolio/${p.id}`)}
                        className={cn(
                          "border-b transition-colors cursor-pointer",
                          needsHelp
                            ? "bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30 hover:bg-red-100"
                            : atRisk
                              ? "bg-amber-50/40 border-amber-100 dark:border-amber-500/30 hover:bg-amber-50 dark:hover:bg-amber-500/15"
                              : "border-line-soft hover:bg-raised",
                        )}
                      >
                        {col("id") && <td className="px-4 py-2.5 font-mono text-xs text-ink-muted">{p.id}</td>}
                        {col("name") && (
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1.5 font-medium text-ink">
                              {p.is_stale && (
                                <span
                                  className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400"
                                  title={
                                    p.plan_last_updated_at
                                      ? `Plan not updated in ${daysSince(p.plan_last_updated_at)} days \u2014 may be out of sync with reality`
                                      : "Plan freshness unknown \u2014 may be out of sync with reality"
                                  }
                                />
                              )}
                              <span>{p.name}</span>
                              {/* PARENT badge — appears when this row has child
                                  projects rolling up under it. Distinct from
                                  initiative-link badges; specifically signals
                                  the program/parent-child relationship. */}
                              {(p.child_count ?? 0) > 0 && (
                                <span
                                  className="rounded bg-brand-navy-100 dark:bg-brand-navy-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-brand-navy-700 dark:text-brand-navy-300"
                                  title={`${p.child_count} child project${p.child_count === 1 ? "" : "s"} roll up under this row`}
                                >
                                  Parent · {p.child_count}
                                </span>
                              )}
                              {/* Child indicator — small chevron + parent id so
                                  the row visually associates with its parent
                                  even without grouping. */}
                              {p.parent_project_id && (
                                <span
                                  className="rounded bg-raised px-1.5 py-0.5 text-[9px] font-medium text-ink-3"
                                  title={`Child of ${p.parent_project_id}`}
                                >
                                  ↳ {p.parent_project_id}
                                </span>
                              )}
                            </div>
                            <div className="mt-0.5 flex gap-1">
                              {ROLE_DOTS.map((r) => {
                                const alloc = p.role_allocations?.[r.key] ?? 0;
                                return alloc > 0 ? (
                                  <span key={r.key} title={`${r.label} ${Math.round(alloc * 100)}%`}
                                    className="inline-block rounded px-1 py-0 text-[8px] font-semibold bg-raised text-ink-muted">{r.short}</span>
                                ) : null;
                              })}
                            </div>
                          </td>
                        )}
                        {col("source") && (
                          <td className="px-4 py-2.5">
                            {p.source === "freshservice" && p.external_id ? (
                              <a
                                href={`https://${FRESHSERVICE_PORTAL}/a/tickets/${p.external_id}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 rounded-full bg-sky-100 dark:bg-sky-500/15 px-2 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-300 hover:bg-sky-200 transition-colors"
                                title={`Freshservice ticket #${p.external_id} — click to open`}
                              >
                                Freshservice · #{p.external_id}
                              </a>
                            ) : (
                              // Direct-entry projects (manual New Project
                              // dialog or seed data) had source=null and
                              // rendered as "—" — indistinguishable from
                              // missing data. Show an explicit pill so the
                              // CFO can tell at a glance which Requested
                              // projects bypassed the Freshservice intake.
                              <span
                                className="inline-block rounded-full bg-raised px-2 py-0.5 text-[10px] font-medium text-ink-3"
                                title="Created directly in the planner (did not come from Freshservice)"
                              >
                                Direct entry
                              </span>
                            )}
                          </td>
                        )}
                        {col("team") && <td className="px-4 py-2.5 text-xs text-ink-muted">{(p as any).team || "\u2014"}</td>}
                        {col("status") && (
                          <td className="px-4 py-2.5">
                            <span className={cn("inline-block rounded-full px-2 py-0.5 text-[10px] font-medium", statusPillClass(p.status))}>
                              {statusLabel(p.status)}
                            </span>
                          </td>
                        )}
                        {col("health") && (() => {
                          const eh = effectiveHealth(p.status, p.health);
                          return (
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-1.5">
                                <span className={cn("inline-block h-2 w-2 rounded-full", eh.dotClass)} />
                                <span className={cn("text-[10px] font-medium", eh.pillClass, "rounded-full px-2 py-0.5")}>
                                  {eh.label}
                                </span>
                              </div>
                            </td>
                          );
                        })()}
                        {col("phase") && (
                          <td className="px-4 py-2.5">
                            {p.current_phase ? (
                              <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${phaseStyle(p.current_phase)}`}>
                                {phaseLabel(p.current_phase)}
                              </span>
                            ) : <span className="text-xs text-ink-faint">{"\u2014"}</span>}
                          </td>
                        )}
                        {col("priority") && <td className="px-4 py-2.5 text-ink-3">{p.priority ?? "\u2014"}</td>}
                        {col("tshirt") && <td className="px-4 py-2.5 text-ink-2">{p.tshirt_size ?? "\u2014"}</td>}
                        {col("est_cost") && <td className="px-4 py-2.5 text-right tabular-nums text-ink-2">{costLabelFor(p, blendedRate)}</td>}
                        {col("brief") && <td className="px-4 py-2.5"><BriefBadge status={p.brief_status} /></td>}
                        {col("jira") && <td className="px-4 py-2.5"><JiraRollupBadge rollup={p.jira_rollup} /></td>}
                        {col("hours") && <td className="px-4 py-2.5 text-right tabular-nums text-ink-2">{p.est_hours > 0 ? p.est_hours.toLocaleString() : "\u2014"}</td>}
                        {col("spec_due") && <td className={cn("px-4 py-2.5 tabular-nums", specDueCellClass(activeSpecDue(p)))}>{formatDate(activeSpecDue(p))}</td>}
                        {col("spec_completed") && <td className="px-4 py-2.5 text-ink-muted tabular-nums">{formatDate(shownSpecCompleted(p))}</td>}
                        {col("start") && <td className="px-4 py-2.5 text-ink-muted tabular-nums">{formatDate(p.start_date)}</td>}
                        {col("end") && <td className="px-4 py-2.5 text-ink-muted tabular-nums">{formatDate(p.end_date)}</td>}
                        {col("slip") && <td className="px-4 py-2.5"><SlipCell days={p.slip_days} /></td>}
                        {col("pct_done") && <td className="px-4 py-2.5 text-right tabular-nums text-ink-2">{Math.round(p.pct_complete * 100)}%</td>}
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-1">
                            {group.key === "requested" && isPmo && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateProject.mutate(
                                      { id: p.id, status: "functional_spec" } as any,
                                      { onSuccess: () => toast.success(`${p.name} approved → Functional Spec`) },
                                    );
                                  }}
                                  className="rounded p-1 text-ink-faint hover:bg-emerald-50 dark:hover:bg-emerald-500/15 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                                  aria-label="Approve"
                                  title="Approve → Functional Spec"
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateProject.mutate(
                                      { id: p.id, status: "declined" } as any,
                                      { onSuccess: () => toast.success(`${p.name} declined`) },
                                    );
                                  }}
                                  className="rounded p-1 text-ink-faint hover:bg-red-50 dark:hover:bg-red-500/15 hover:text-red-500 transition-colors"
                                  aria-label="Decline"
                                  title="Decline"
                                >
                                  <XCircle className="h-3.5 w-3.5" />
                                </button>
                              </>
                            )}
                            {isPmo && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(p.id);
                              }}
                              className="rounded p-1 text-ink-faint hover:bg-red-50 dark:hover:bg-red-500/15 hover:text-red-500 transition-colors"
                              aria-label="Delete project"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
        {!isLoading && groups.every((g) => g.projects.length === 0) && (
          <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
            No projects found. Click "Add project" to get started.
          </div>
        )}
      </div>

      <EditProjectDialog
        project={editProject}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      {/* spacer so badge component below stays out of return JSX */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl bg-surface p-6 shadow-lg">
            <h3 className="text-sm font-semibold text-ink">Delete project?</h3>
            <p className="mt-2 text-sm text-ink-3">
              Remove <strong>{deleteConfirm}</strong> and all its allocations? This cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg border border-line px-3 py-1.5 text-sm font-medium text-ink-3 hover:bg-raised transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteProject.isPending}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleteProject.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// BriefBadge moved to @/components/ui/BriefBadge so the
// DepartmentPortfolio "Pending Approval" section can render the same
// pill without duplicating the logic.
